<template>
	<div ref="modalRef" class="modal-backdrop" :data-context="mixpanelContext">
		<div class="modal display-block" role="button" tabindex="0" @keyup.esc="close">
			<div class="modal-dialog">
				<div class="modal-container" style="transform: scale(1) !important">
					<div v-if="!props.fullWidth" :class="props.containerClasses">
						<div ref="modalContentRef" :class="props.contentClasses">
							<slot />
						</div>
					</div>
					<template v-else>
						<slot />
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
export default {
	name: 'BaseModal'
};
</script>

<script setup lang="ts">
import { ClassValue, computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useAnalyticsProperties } from '@utils/composables/use-analytics-properties';
import { useMixpanel } from '@utils/composables/use-mixpanel';

interface Props {
	dataContext?: string;
	closeable?: boolean;
	suppressDefaultMxpEvent?: boolean;
	containerClasses?: ClassValue;
	contentClasses?: ClassValue;
	fullWidth?: boolean;
}

interface Emits {
	(e: 'modal-close'): void;
}

const props = withDefaults(defineProps<Props>(), {
	dataContext: undefined,
	closeable: true,
	suppressDefaultMxpEvent: false,
	containerClasses: 'grid-md gutters-300-md',
	contentClasses: 'col-8-md col-offset-2-md col-6-lg col-offset-3-lg',
	fullWidth: false
});

const emits = defineEmits<Emits>();

const { trackMixpanelView } = useMixpanel();
const { getViewProperties } = useAnalyticsProperties();

const currentInstance = getCurrentInstance();

const mainNav: HTMLElement | null = document.getElementById('main-nav');
const mainContent: HTMLElement | null = document.getElementById('main-content');
const footer: HTMLElement | null = document.getElementById('footer');

const body = ref<HTMLCollectionOf<HTMLBodyElement>>(document.getElementsByTagName('body'));
const modalRef = ref<HTMLElement | null>(null);
const modalContentRef = ref<HTMLElement | null>(null);
onClickOutside(modalContentRef, close);

const mixpanelContext = computed((): string => props.dataContext ?? getModalName());

function getModalName(): string {
	let parentModalName = currentInstance?.proxy?.$options.name;
	let parentComponent = currentInstance?.proxy?.$parent;

	for (let i = 0; i < 3; i++) {
		if (parentComponent) {
			if (parentComponent.$options.name?.includes('Modal')) {
				parentModalName = parentComponent.$options.name;
			}
			parentComponent = parentComponent.$parent;
		}
	}

	return parentModalName ?? 'Modal';
}

function measureScrollbarWidth(): number {
	const measuringDiv = document.createElement('div');
	measuringDiv.style.width = '100px';
	measuringDiv.style.height = '100px';
	measuringDiv.style.overflow = 'scroll';
	measuringDiv.style.position = 'absolute';
	measuringDiv.style.top = '-9999px';
	document.body.appendChild(measuringDiv);
	const scrollbarWidth = measuringDiv.offsetWidth - measuringDiv.clientWidth;
	document.body.removeChild(measuringDiv);
	const scrollbarVisible = window.innerHeight < document.body.scrollHeight;
	return scrollbarVisible ? scrollbarWidth : 0;
}

function close(): void {
	if (props.closeable) {
		emits('modal-close');
	}
}

onMounted(() => {
	if (!props.suppressDefaultMxpEvent) {
		trackMixpanelView('Modal', { ...getViewProperties(), 'View Name': mixpanelContext.value });
	}

	// We must focus the container with click/keyup handlers in order for keyup.esc to work.
	if (props.closeable) {
		(modalRef.value?.children[0] as HTMLElement).focus();
	}
});

onUnmounted(() => {
	if (body.value) {
		body.value[0].style.overflowY = 'hidden';
	}

	// first timeout waits until modal fade animation finishes to remove scrollbar padding
	setTimeout(() => {
		if (footer) {
			footer.style.paddingRight = '';
		}
		if (mainContent) {
			mainContent.style.paddingRight = '';
			if (body.value) {
				body.value[0].style.overflowY = '';
			}
		}
		if (mainNav) {
			mainNav.style.paddingRight = '';
		}

		//second timeout waits until nav transition animation finishes to remove inline transition, otherwise nav bounces
		setTimeout(() => {
			if (mainNav) {
				mainNav.style.transition = '';
			}
		}, 300);
	}, 500);
});

function setup(): void {
	body.value = document.getElementsByTagName('body');
	const scrollbarWidth = measureScrollbarWidth() + 'px';

	if (mainContent) {
		mainContent.style.paddingRight = scrollbarWidth;
	}

	if (footer) {
		footer.style.paddingRight = scrollbarWidth;
	}

	if (mainNav) {
		mainNav.style.transition = 'none';
		mainNav.style.paddingRight = scrollbarWidth;
	}
}

setup();
</script>
